import { useEffect, useState } from 'react'

// The viewer's own device timezone (browser-detected) — the clock always
// shows local time for whoever is looking at the dashboard, not a stored
// profile setting, so it's correct even when an admin is viewing on behalf
// of another timezone's org.
const DEVICE_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

/** A ring of 60 tick marks (12 longer "hour" ticks, 48 shorter "minute"
 * ticks) drawn as a static clock face behind the digital readout. */
function ClockTicks() {
  const ticks = Array.from({ length: 60 })
  return (
    <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full" aria-hidden="true">
      {ticks.map((_, i) => {
        const isMajor = i % 5 === 0
        const angle = (i * 6 * Math.PI) / 180
        const outer = 96
        const inner = isMajor ? 82 : 90
        const x1 = 100 + outer * Math.sin(angle)
        const y1 = 100 - outer * Math.cos(angle)
        const x2 = 100 + inner * Math.sin(angle)
        const y2 = 100 - inner * Math.cos(angle)
        return (
          <line
            key={i}
            x1={x1} y1={y1} x2={x2} y2={y2}
            stroke={isMajor ? '#c7ccd4' : '#e5e7eb'}
            strokeWidth={isMajor ? 2 : 1}
            strokeLinecap="round"
          />
        )
      })}
    </svg>
  )
}

export default function LiveClock() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])

  const opts: Intl.DateTimeFormatOptions = { timeZone: DEVICE_TIMEZONE }
  const timeMain = now.toLocaleTimeString('en-US', { ...opts, hour: '2-digit', minute: '2-digit', hour12: true })
  const seconds = now.toLocaleTimeString('en-US', { ...opts, second: '2-digit' }).slice(-2).padStart(2, '0')
  const dayStr = now.toLocaleDateString('en-US', { ...opts, weekday: 'long' })
  const dateStr = now.toLocaleDateString('en-US', { ...opts, month: 'long', day: 'numeric', year: 'numeric' })

  return (
    <div className="relative h-48 w-48 sm:h-52 sm:w-52">
      <ClockTicks />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
        <p className="text-xs text-gray-400">{dayStr}</p>
        <p className="text-2xl font-bold text-gray-900 tabular-nums">
          {timeMain.replace(/ (AM|PM)/, '')}
          <span className="text-sm text-gray-400">:{seconds}</span>{' '}
          <span className="text-sm font-semibold text-gray-500">{timeMain.slice(-2)}</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">{dateStr}</p>
        <p className="text-[11px] text-gray-300 mt-0.5">{DEVICE_TIMEZONE}</p>
      </div>
    </div>
  )
}
